import type { DevicesDevice, DevicesDeviceState } from '@smarthome/api';
import { useDebounce } from '@smarthome/ui';
import { useEffect, useRef, useState } from 'react';

import { Slider } from '@/components/ui';

export interface WindowCoveringControlsProps {
  device: DevicesDevice;
  onUpdateState: (state: DevicesDeviceState) => void;
  disabled?: boolean;
}

// How long to ignore incoming state updates after a position change (window covering movement time)
const MOVEMENT_IGNORE_DURATION_MS = 20000;

/**
 * Window covering controls component.
 *
 * Position mapping:
 * - 0% = fully open
 * - 100% = fully closed
 *
 * For window coverings without position capability, we still use closedPercentage
 * with 0 (open) and 100 (closed) values.
 */
export function WindowCoveringControls({
  device,
  onUpdateState,
  disabled,
}: WindowCoveringControlsProps) {
  const capabilities = device.capabilities?.windowCovering;
  const windowCoveringState = device.state?.windowCovering;
  const supportsPosition = capabilities?.position === true;

  // Local state for position slider
  const rawPosition = windowCoveringState?.closedPercentage ?? 0;
  const [localPosition, setLocalPosition] = useState(rawPosition);
  const debouncedPosition = useDebounce(localPosition, 300);

  // Track if we initiated the change to avoid sync loops
  const isUserChange = useRef(false);
  const lastSentPosition = useRef(debouncedPosition);
  // Track when we last sent a position change to ignore polling during movement
  const lastMovementTime = useRef<number>(0);

  // Sync local state with device state when it changes externally
  // Ignore updates for 20s after initiating movement to avoid polling mid-movement
  useEffect(() => {
    const timeSinceMovement = Date.now() - lastMovementTime.current;
    const isWithinMovementWindow = timeSinceMovement < MOVEMENT_IGNORE_DURATION_MS;

    if (!isUserChange.current && !isWithinMovementWindow) {
      setLocalPosition(rawPosition);
    }
  }, [rawPosition]);

  // Send debounced position updates
  useEffect(() => {
    if (isUserChange.current && debouncedPosition !== lastSentPosition.current) {
      lastSentPosition.current = debouncedPosition;
      lastMovementTime.current = Date.now();
      onUpdateState({ windowCovering: { closedPercentage: debouncedPosition } });
      isUserChange.current = false;
    }
  }, [debouncedPosition, onUpdateState]);

  const handlePositionChange = (value: number) => {
    isUserChange.current = true;
    setLocalPosition(value);
  };

  const handleQuickOpen = () => {
    if (supportsPosition) {
      // Set position to 0 (fully open)
      isUserChange.current = true;
      setLocalPosition(0);
      lastSentPosition.current = 0;
      lastMovementTime.current = Date.now();
      onUpdateState({ windowCovering: { closedPercentage: 0 } });
    } else {
      // Use closedPercentage: 0 = fully open
      lastMovementTime.current = Date.now();
      onUpdateState({ windowCovering: { closedPercentage: 0 } });
    }
  };

  const handleQuickClose = () => {
    if (supportsPosition) {
      // Set position to 100 (fully closed)
      isUserChange.current = true;
      setLocalPosition(100);
      lastSentPosition.current = 100;
      lastMovementTime.current = Date.now();
      onUpdateState({ windowCovering: { closedPercentage: 100 } });
    } else {
      // Use closedPercentage: 100 = fully closed
      lastMovementTime.current = Date.now();
      onUpdateState({ windowCovering: { closedPercentage: 100 } });
    }
  };

  // Determine current state for button highlighting
  const isFullyOpen = supportsPosition ? localPosition === 0 : rawPosition === 0;
  const isFullyClosed = supportsPosition ? localPosition === 100 : rawPosition === 100;

  return (
    <div className="space-y-3">
      {/* Position slider (only if supported) */}
      {supportsPosition && (
        <div>
          <Slider
            label="Position"
            min={0}
            max={100}
            value={localPosition}
            onChange={handlePositionChange}
            formatValue={(v) => (v === 0 ? 'Open' : v === 100 ? 'Closed' : `${v}% closed`)}
            disabled={disabled}
          />
        </div>
      )}

      {/* Control - segmented buttons */}
      <div>
        <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-zinc-300">
          Control
        </label>
        <div className="inline-flex overflow-hidden rounded-lg border border-gray-200 dark:border-white/[0.06]">
          <button
            type="button"
            onClick={handleQuickOpen}
            disabled={disabled}
            className={`px-4 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
              isFullyOpen
                ? 'bg-blue-500 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:bg-zinc-800'
            }`}
          >
            Open
          </button>
          <button
            type="button"
            onClick={handleQuickClose}
            disabled={disabled}
            className={`border-l border-gray-200 px-4 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/[0.06] ${
              isFullyClosed
                ? 'bg-blue-500 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:bg-zinc-800'
            }`}
          >
            Close
          </button>
        </div>
        {!supportsPosition && (
          <p className="mt-2 text-xs text-gray-500 dark:text-zinc-500">
            This window covering only supports open/close commands.
          </p>
        )}
      </div>
    </div>
  );
}
